<!-- Gemini 3 pro 写的，我懒得写这样神秘的东西（ -->

<template>
    <div v-if="isVisible" ref="boxRef" class="bouncing-modal" :style="boxStyle" @mouseenter="isPaused = true"
        @mouseleave="isPaused = false">
        <div class="close-btn" @click="closeModal" title="关闭">
            &times;
        </div>

        <div class="content">
            <slot></slot>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';


// props 定义
const props = defineProps({
    // 初始速度 (像素/帧)
    speed: {
        type: Number,
        default: 2
    },
    // 初始角度 (0-360度)
    initialAngle: {
        type: Number,
        default: 45
    },
    // 是否允许鼠标悬停时暂停
    hoverPause: {
        type: Boolean,
        default: true
    }
});

// 状态变量
const isVisible = ref(true);
const boxRef = ref(null);
const x = ref(0);
const y = ref(0);
const velocity = ref({ x: 0, y: 0 });
const containerDim = ref({ w: 0, h: 0 });
const boxDim = ref({ w: 0, h: 0 });
const isPaused = ref(false);

let animationFrameId = null;
let resizeObserver = null;

// 计算样式
const boxStyle = computed(() => ({
    transform: `translate3d(${x.value}px, ${y.value}px, 0)`,
    position: 'absolute',
    left: 0,
    top: 0,
    willChange: 'transform' // 性能优化
}));

// 初始化速度向量
const initVelocity = () => {
    const rad = (props.initialAngle * Math.PI) / 180;
    velocity.value = {
        x: props.speed * Math.cos(rad),
        y: props.speed * Math.sin(rad)
    };
};

// 更新容器和自身尺寸
const updateDimensions = () => {
    if (boxRef.value && boxRef.value.parentElement) {
        const parent = boxRef.value.parentElement;
        containerDim.value = {
            w: parent.clientWidth,
            h: parent.clientHeight
        };
        boxDim.value = {
            w: boxRef.value.offsetWidth,
            h: boxRef.value.offsetHeight
        };
    }
};

// 核心动画循环
const animate = () => {
    if (!isVisible.value) return;

    if (!isPaused.value || !props.hoverPause) {
        let nextX = x.value + velocity.value.x;
        let nextY = y.value + velocity.value.y;

        const maxW = containerDim.value.w - boxDim.value.w;
        const maxH = containerDim.value.h - boxDim.value.h;

        // X轴碰撞检测 (左右墙壁)
        if (nextX <= 0) {
            nextX = 0;
            velocity.value.x = -velocity.value.x; // 反转X速度
        } else if (nextX >= maxW) {
            nextX = maxW;
            velocity.value.x = -velocity.value.x;
        }

        // Y轴碰撞检测 (上下墙壁)
        if (nextY <= 0) {
            nextY = 0;
            velocity.value.y = -velocity.value.y; // 反转Y速度
        } else if (nextY >= maxH) {
            nextY = maxH;
            velocity.value.y = -velocity.value.y;
        }

        x.value = nextX;
        y.value = nextY;
    }

    animationFrameId = requestAnimationFrame(animate);
};

// 关闭功能
const closeModal = () => {
    isVisible.value = false;
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
};

onMounted(async () => {
    await nextTick();

    // 1. 确保父容器是定位元素，否则弹窗会飞出去
    if (boxRef.value && boxRef.value.parentElement) {
        const parentStyle = window.getComputedStyle(boxRef.value.parentElement);
        if (parentStyle.position === 'static') {
            console.warn('BouncingModal: 父容器需要设置 position: relative/absolute/fixed，否则边界检测可能失效。');
        }
    }

    // 2. 监听尺寸变化（处理窗口缩放）
    resizeObserver = new ResizeObserver(() => {
        updateDimensions();
        // 简单的防止缩放导致元素卡在墙外
        if (x.value > containerDim.value.w - boxDim.value.w) x.value = containerDim.value.w - boxDim.value.w;
        if (y.value > containerDim.value.h - boxDim.value.h) y.value = containerDim.value.h - boxDim.value.h;
    });

    if (boxRef.value && boxRef.value.parentElement) {
        resizeObserver.observe(boxRef.value.parentElement);
        resizeObserver.observe(boxRef.value);
    }

    // 3. 启动
    updateDimensions();
    // 初始位置设为容器中心 (可选)
    x.value = (containerDim.value.w - boxDim.value.w) / 2;
    y.value = (containerDim.value.h - boxDim.value.h) / 2;

    initVelocity();
    animate();
});

onBeforeUnmount(() => {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    if (resizeObserver) resizeObserver.disconnect();
});
</script>

<style scoped>
@keyframes rt {
    0% {
        transform: rotateZ(0deg) scale(0) rotateX(0);
        /* transform: rotateY(0deg); */
        /* transform: scaleY(0); */
    }

    30% {
        transform: rotateY(360deg) scale(1) rotateX(360deg) scaleX(5);
    }

    100% {
        /* transform: rotateZ(360deg); */
        filter: contrast(100%);
        filter: hue-rotate(1800deg);
        transform: rotateY(360deg) scale(1) rotateX(360deg);
        /* transform: scaleY(1); */
    }
}

@font-face {
    font-family: 'Comic Sans';
    src: url("./fonts/LDFComicSans.ttf") format('truetype');
}

@font-face {
    font-family: 'Smiley Sans';
    src: url("./fonts/SmileySans-Oblique.ttf") format('truetype');
}

.bouncing-modal:hover div {
    animation: rt 1s;
}

.bouncing-modal:hover {
    border-width: 10px;
    padding: 20px;
}

.bouncing-modal {
    font-family: 'Comic Sans', 'Smiley Sans', Arial, sans-serif;
    /* 基本样式，可根据需要修改 */
    /* background: #0F0; */
    border: 5px solid rgb(0, 183, 255);
    border-style: outset;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
    /* border-radius: 8px; */
    /* color: yellow; */
    padding: 10px;
    min-width: min(150px, 2vw);
    min-height: min(200px, 1vh);
    max-width: min(600px, 30vw);
    max-height: min(800px, 10vh);
    overflow: hidden;
    z-index: 10000;
    user-select: none;
    transition: border, padding 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    pointer-events: all;
    /* 防止拖动时选中文字 */
}

.close-btn {
    position: absolute;
    top: 5px;
    right: 8px;
    cursor: pointer;
    font-size: 18px;
    pointer-events: all;
    line-height: 1;
    color: #999;
    font-weight: bold;
}

.close-btn:hover {
    color: #ff4d4f;
}

.content {
    margin-top: 15px;
    /* 为关闭按钮留出空间 */
}
</style>

